import { useCallback, useEffect, useRef, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  adminTrainerVerificationsApi,
  type TrainerVerificationForAdmin,
} from '../api/trainerVerifications';
import { translateApiError } from '../auth/errors';

/**
 * F43-FC §6.4 — shared trainer-moderation orchestration. Both the dedicated
 * `/trainer-verifications/:userId` page and the platform-user Trainer panel call
 * this, so the fetch + 6 action handlers (all REUSED `adminTrainerVerificationsApi`
 * calls) live in exactly one place. F09 v2 retired the profile approve/reject
 * (the trainer tick is an F09 request); per-cert review + moderation remain.
 * The hook holds `detail`; callers render it with the extracted
 * `components/trainer/*` UI.
 *
 * Pass `undefined` for `userId` to keep the hook inert (no fetch, no
 * presigned-GET S3 access log) — the platform-user panel does this for a
 * non-trainer user or a read-only (non-reviewer) actor.
 */
export function useTrainerModeration(userId: string | undefined) {
  const { t } = useI18n();
  const [detail, setDetail] = useState<TrainerVerificationForAdmin | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyCertId, setBusyCertId] = useState<string | null>(null);
  const [linksExpired, setLinksExpired] = useState(false);
  const expiredRef = useRef(false);

  const fetchDetail = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    setLinksExpired(false);
    expiredRef.current = false;
    try {
      const data = await adminTrainerVerificationsApi.detail(userId);
      if (data === null) setNotFound(true);
      else setDetail(data);
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [userId, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchDetail();
  }, [fetchDetail]);

  const handleLinkExpired = useCallback(() => {
    if (!expiredRef.current) {
      expiredRef.current = true;
      setLinksExpired(true);
    }
  }, []);

  const suspend = useCallback(
    async (reason: string) => {
      if (!userId) return;
      setDetail(await adminTrainerVerificationsApi.suspendTrainer(userId, { reason }));
    },
    [userId],
  );

  const reinstate = useCallback(async () => {
    if (!userId) return;
    setDetail(await adminTrainerVerificationsApi.reinstateTrainer(userId));
  }, [userId]);

  const forceDeactivate = useCallback(
    async (reason: string) => {
      if (!userId) return;
      setDetail(await adminTrainerVerificationsApi.forceDeactivateTrainer(userId, { reason }));
    },
    [userId],
  );

  const verifyCert = useCallback(
    async (certId: string) => {
      setBusyCertId(certId);
      try {
        await adminTrainerVerificationsApi.verifyCert(certId);
        await fetchDetail();
      } finally {
        setBusyCertId(null);
      }
    },
    [fetchDetail],
  );

  const rejectCert = useCallback(
    async (certId: string, reason: string) => {
      await adminTrainerVerificationsApi.rejectCert(certId, { rejectionReason: reason });
      await fetchDetail();
    },
    [fetchDetail],
  );

  const revokeCert = useCallback(
    async (certId: string, reason: string) => {
      await adminTrainerVerificationsApi.revokeCert(certId, { rejectionReason: reason });
      await fetchDetail();
    },
    [fetchDetail],
  );

  return {
    detail,
    loading,
    notFound,
    error,
    busyCertId,
    linksExpired,
    fetchDetail,
    handleLinkExpired,
    suspend,
    reinstate,
    forceDeactivate,
    verifyCert,
    rejectCert,
    revokeCert,
  };
}
